import { useEffect, useState } from 'react'
import { toast } from 'react-toastify'
import { FaWhatsapp } from 'react-icons/fa'
import { fetchPostalCode } from '@/lib/api/fetch-postal-code'
import {
    MdAccountBalance,
    MdEmail,
    MdLanguage,
    MdLocationOn,
    MdPhone,
    MdSupportAgent,
} from 'react-icons/md'
import { getPartner, updatePartner } from '@/lib/api/partners/partners-api'
import { PersonType } from '@/lib/api/common/interfaces/partner.interface'
import { setCredentials } from '@/store/slices/authSlice'
import { useAppDispatch, useAppSelector, useAuth } from '@/store/hooks'
import { formatCpf, formatCnpj, formatPhone, formatCep } from '../account-utils'

/**
 * Aba "Minha empresa" — dados do PARCEIRO (so existe em conta de parceiro).
 *
 * Carrega ao montar. Antes o disparo estava em tres lugares: um efeito que
 * olhava `activeTab`, o clique da aba e um atalho vindo de fora.
 */
export default function MyCompanyTab() {
    const auth = useAppSelector(state => state.auth)
    const { user } = useAuth()
    const dispatch = useAppDispatch()

    // Partner form (tab "Minha Empresa")
    const [partnerFormData, setPartnerFormData] = useState({
        name: '',
        tradingName: '',
        email: '',
        phone: '',
        personType: 'JURIDICA' as PersonType,
        cpf: '',
        cnpj: '',
        rg: '',
        rgIssuer: '',
        stateRegistration: '',
        municipalRegistration: '',
        street: '',
        number: '',
        complement: '',
        neighborhood: '',
        city: '',
        state: '',
        zipCode: '',
        country: 'BR',
        bankCode: '',
        bankName: '',
        bankAgency: '',
        bankAccount: '',
        pixKey: '',
        pixKeyType: '',
        contactName: '',
        contactPhone: '',
        contactEmail: '',
        website: '',
        whatsappNumber: '',
        notes: '',
    })
    const [loadingPartner, setLoadingPartner] = useState(false)
    const [savingPartner, setSavingPartner] = useState(false)
    const [loadingPartnerCep, setLoadingPartnerCep] = useState(false)

    const loadPartnerFullData = async () => {
        if (!user?.partner?.id) return
        setLoadingPartner(true)
        try {
            const response = await getPartner(user.partner.id)

            if (response.success && response.result) {
                const p = response.result
                setPartnerFormData({
                    name: p.name || '',
                    tradingName: p.tradingName || '',
                    email: p.email || '',
                    phone: p.phone || '',
                    personType: p.personType || 'JURIDICA',
                    cpf: p.cpf || '',
                    cnpj: p.cnpj || '',
                    rg: p.rg || '',
                    rgIssuer: p.rgIssuer || '',
                    stateRegistration: p.stateRegistration || '',
                    municipalRegistration: p.municipalRegistration || '',
                    street: p.street || '',
                    number: p.number || '',
                    complement: p.complement || '',
                    neighborhood: p.neighborhood || '',
                    city: p.city || '',
                    state: p.state || '',
                    zipCode: p.zipCode || '',
                    country: p.country || 'BR',
                    bankCode: p.bankCode || '',
                    bankName: p.bankName || '',
                    bankAgency: p.bankAgency || '',
                    bankAccount: p.bankAccount || '',
                    pixKey: p.pixKey || '',
                    pixKeyType: p.pixKeyType || '',
                    contactName: p.contactName || '',
                    contactPhone: p.contactPhone || '',
                    contactEmail: p.contactEmail || '',
                    website: p.website || '',
                    whatsappNumber: p.whatsappNumber || '',
                    notes: p.notes || '',
                })
            }
        } catch (error) {
            toast.error('Erro ao carregar dados da empresa')
        } finally {
            setLoadingPartner(false)
        }
    }

    const handleUpdatePartner = async () => {
        if (!user?.partner?.id) return

        if (!partnerFormData.name.trim()) {
            toast.warning('Preencha o nome/razão social')
            return
        }

        setSavingPartner(true)
        try {
            const cleanData: any = {
                name: partnerFormData.name.trim(),
                tradingName: partnerFormData.tradingName.trim() || undefined,
                email: partnerFormData.email.trim() || undefined,
                phone: partnerFormData.phone.trim() || undefined,
                personType: partnerFormData.personType,
                cpf: partnerFormData.cpf.replace(/\D/g, '') || undefined,
                cnpj: partnerFormData.cnpj.replace(/\D/g, '') || undefined,
                rg: partnerFormData.rg.trim() || undefined,
                rgIssuer: partnerFormData.rgIssuer.trim() || undefined,
                stateRegistration:
                    partnerFormData.stateRegistration.trim() || undefined,
                municipalRegistration:
                    partnerFormData.municipalRegistration.trim() || undefined,
                street: partnerFormData.street.trim() || undefined,
                number: partnerFormData.number.trim() || undefined,
                complement: partnerFormData.complement.trim() || undefined,
                neighborhood: partnerFormData.neighborhood.trim() || undefined,
                city: partnerFormData.city.trim() || undefined,
                state: partnerFormData.state.trim() || undefined,
                zipCode:
                    partnerFormData.zipCode.replace(/\D/g, '') || undefined,
                country: partnerFormData.country || undefined,
                bankCode: partnerFormData.bankCode.trim() || undefined,
                bankName: partnerFormData.bankName.trim() || undefined,
                bankAgency: partnerFormData.bankAgency.trim() || undefined,
                bankAccount: partnerFormData.bankAccount.trim() || undefined,
                pixKey: partnerFormData.pixKey.trim() || undefined,
                pixKeyType: partnerFormData.pixKeyType.trim() || undefined,
                contactName: partnerFormData.contactName.trim() || undefined,
                contactPhone: partnerFormData.contactPhone.trim() || undefined,
                contactEmail: partnerFormData.contactEmail.trim() || undefined,
                website: partnerFormData.website.trim() || undefined,
                whatsappNumber:
                    partnerFormData.whatsappNumber.trim() || undefined,
                notes: partnerFormData.notes.trim() || undefined,
            }

            const response = await updatePartner(user.partner.id, cleanData)

            if (response.success) {
                toast.success('Dados da empresa atualizados com sucesso')
                dispatch(
                    setCredentials({
                        ...auth,
                        user: {
                            ...user,
                            partner: {
                                ...user.partner,
                                name: partnerFormData.name.trim(),
                                tradingName:
                                    partnerFormData.tradingName.trim() || null,
                                cnpj:
                                    partnerFormData.cnpj.replace(/\D/g, '') ||
                                    null,
                                cpf:
                                    partnerFormData.cpf.replace(/\D/g, '') ||
                                    null,
                                personType: partnerFormData.personType,
                                street: partnerFormData.street.trim() || null,
                                number: partnerFormData.number.trim() || null,
                                neighborhood:
                                    partnerFormData.neighborhood.trim() || null,
                                city: partnerFormData.city.trim() || null,
                                state: partnerFormData.state.trim() || null,
                                zipCode:
                                    partnerFormData.zipCode.replace(
                                        /\D/g,
                                        ''
                                    ) || null,
                            },
                        },
                    })
                )
            } else {
                toast.error(response.error || 'Erro ao atualizar dados')
            }
        } catch (error) {
            toast.error('Erro ao atualizar dados da empresa')
        } finally {
            setSavingPartner(false)
        }
    }

    const handlePartnerCepChange = async (cep: string) => {
        const cleanCep = cep.replace(/\D+/g, '')

        if (cleanCep.length !== 8) return

        setLoadingPartnerCep(true)
        try {
            const address = await fetchPostalCode(cleanCep)
            setPartnerFormData(prev => ({
                ...prev,
                zipCode: address.cep || prev.zipCode,
                street: address.logradouro || prev.street,
                neighborhood: address.bairro || prev.neighborhood,
                city: address.localidade || prev.city,
                state: address.uf || prev.state,
            }))
        } catch (error) {
            toast.error('CEP não encontrado')
        } finally {
            setLoadingPartnerCep(false)
        }
    }

    useEffect(() => {
        void loadPartnerFullData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.partner?.id])

    return (
            <div className="flex flex-col gap-6">
                {loadingPartner ? (
                    <div className="flex justify-center py-12">
                        <span className="loading loading-spinner loading-lg"></span>
                    </div>
                ) : (
                    <>
                        <h2 className="text-xl font-bold text-base-content">
                            Dados da Empresa
                        </h2>

                        {/* Tipo de Pessoa */}
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text font-semibold">
                                    Tipo de Pessoa
                                </span>
                            </label>
                            <div className="flex gap-4">
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        className="radio radio-secondary"
                                        checked={
                                            partnerFormData.personType ===
                                            'JURIDICA'
                                        }
                                        onChange={() =>
                                            setPartnerFormData({
                                                ...partnerFormData,
                                                personType:
                                                    'JURIDICA',
                                            })
                                        }
                                    />
                                    <span>Pessoa Jurídica</span>
                                </label>
                                <label className="flex items-center gap-2 cursor-pointer">
                                    <input
                                        type="radio"
                                        className="radio radio-secondary"
                                        checked={
                                            partnerFormData.personType ===
                                            'FISICA'
                                        }
                                        onChange={() =>
                                            setPartnerFormData({
                                                ...partnerFormData,
                                                personType:
                                                    'FISICA',
                                            })
                                        }
                                    />
                                    <span>Pessoa Física</span>
                                </label>
                            </div>
                        </div>

                        {/* Dados principais */}
                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    {partnerFormData.personType ===
                                    'JURIDICA'
                                        ? 'Razão Social'
                                        : 'Nome Completo'}{' '}
                                    <span className="text-error">
                                        *
                                    </span>
                                </span>
                            </label>
                            <input
                                type="text"
                                placeholder={
                                    partnerFormData.personType ===
                                    'JURIDICA'
                                        ? 'Razão Social'
                                        : 'Nome Completo'
                                }
                                className="input input-bordered w-full"
                                value={partnerFormData.name}
                                onChange={e =>
                                    setPartnerFormData({
                                        ...partnerFormData,
                                        name: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    Nome Fantasia
                                </span>
                            </label>
                            <input
                                type="text"
                                placeholder="Nome Fantasia"
                                className="input input-bordered w-full"
                                value={
                                    partnerFormData.tradingName
                                }
                                onChange={e =>
                                    setPartnerFormData({
                                        ...partnerFormData,
                                        tradingName:
                                            e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="form-control w-full sm:flex-1">
                                <label className="label">
                                    <span className="label-text">
                                        Email
                                    </span>
                                </label>
                                <div className="relative">
                                    <MdEmail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                                    <input
                                        type="email"
                                        placeholder="email@empresa.com"
                                        className="input input-bordered w-full pl-10"
                                        value={
                                            partnerFormData.email
                                        }
                                        onChange={e =>
                                            setPartnerFormData({
                                                ...partnerFormData,
                                                email: e.target
                                                    .value,
                                            })
                                        }
                                    />
                                </div>
                            </div>
                            <div className="form-control w-full sm:flex-1">
                                <label className="label">
                                    <span className="label-text">
                                        Telefone
                                    </span>
                                </label>
                                <div className="relative">
                                    <MdPhone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                                    <input
                                        type="tel"
                                        placeholder="(00) 00000-0000"
                                        className="input input-bordered w-full pl-10"
                                        value={formatPhone(
                                            partnerFormData.phone
                                        )}
                                        onChange={e => {
                                            const raw =
                                                e.target.value
                                                    .replace(
                                                        /\D/g,
                                                        ''
                                                    )
                                                    .slice(
                                                        0,
                                                        11
                                                    )
                                            setPartnerFormData({
                                                ...partnerFormData,
                                                phone: raw,
                                            })
                                        }}
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    WhatsApp
                                </span>
                            </label>
                            <div className="relative">
                                <FaWhatsapp className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                                <input
                                    type="tel"
                                    placeholder="+55 (00) 00000-0000"
                                    className="input input-bordered w-full pl-10"
                                    value={formatPhone(
                                        partnerFormData.whatsappNumber
                                    )}
                                    onChange={e => {
                                        const raw =
                                            e.target.value
                                                .replace(
                                                    /\D/g,
                                                    ''
                                                )
                                                .slice(0, 11)
                                        setPartnerFormData({
                                            ...partnerFormData,
                                            whatsappNumber: raw,
                                        })
                                    }}
                                />
                            </div>
                        </div>

                        {/* Documentos */}
                        <div className="divider">
                            <span className="text-sm text-base-content/70">
                                Documentos
                            </span>
                        </div>

                        {partnerFormData.personType ===
                        'JURIDICA' ? (
                            <>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <div className="form-control w-full sm:flex-1">
                                        <label className="label">
                                            <span className="label-text">
                                                CNPJ
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="00.000.000/0000-00"
                                            className="input input-bordered w-full"
                                            value={formatCnpj(
                                                partnerFormData.cnpj
                                            )}
                                            onChange={e => {
                                                const raw =
                                                    e.target.value
                                                        .replace(
                                                            /\D/g,
                                                            ''
                                                        )
                                                        .slice(
                                                            0,
                                                            14
                                                        )
                                                setPartnerFormData(
                                                    {
                                                        ...partnerFormData,
                                                        cnpj: raw,
                                                    }
                                                )
                                            }}
                                        />
                                    </div>
                                    <div className="form-control w-full sm:flex-1">
                                        <label className="label">
                                            <span className="label-text">
                                                Inscrição
                                                Estadual{' '}
                                            </span>
                                        </label>
                                        <input
                                            type="text"
                                            placeholder="Inscrição Estadual"
                                            className="input input-bordered w-full"
                                            value={
                                                partnerFormData.stateRegistration
                                            }
                                            onChange={e =>
                                                setPartnerFormData(
                                                    {
                                                        ...partnerFormData,
                                                        stateRegistration:
                                                            e
                                                                .target
                                                                .value,
                                                    }
                                                )
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="form-control w-full sm:w-1/2">
                                    <label className="label">
                                        <span className="label-text">
                                            Inscrição
                                            Municipal{' '}
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="Inscrição Municipal"
                                        className="input input-bordered w-full"
                                        value={
                                            partnerFormData.municipalRegistration
                                        }
                                        onChange={e =>
                                            setPartnerFormData({
                                                ...partnerFormData,
                                                municipalRegistration:
                                                    e.target
                                                        .value,
                                            })
                                        }
                                    />
                                </div>
                            </>
                        ) : (
                            <div className="flex flex-col sm:flex-row gap-4">
                                <div className="form-control w-full sm:flex-1">
                                    <label className="label">
                                        <span className="label-text">
                                            CPF
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="000.000.000-00"
                                        className="input input-bordered w-full"
                                        value={formatCpf(
                                            partnerFormData.cpf
                                        )}
                                        onChange={e => {
                                            const raw =
                                                e.target.value
                                                    .replace(
                                                        /\D/g,
                                                        ''
                                                    )
                                                    .slice(
                                                        0,
                                                        11
                                                    )
                                            setPartnerFormData({
                                                ...partnerFormData,
                                                cpf: raw,
                                            })
                                        }}
                                    />
                                </div>
                                <div className="form-control w-full sm:flex-1">
                                    <label className="label">
                                        <span className="label-text">
                                            RG
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="RG"
                                        className="input input-bordered w-full"
                                        value={
                                            partnerFormData.rg
                                        }
                                        onChange={e =>
                                            setPartnerFormData({
                                                ...partnerFormData,
                                                rg: e.target
                                                    .value,
                                            })
                                        }
                                    />
                                </div>
                                <div className="form-control w-full sm:w-32">
                                    <label className="label">
                                        <span className="label-text">
                                            Emissor
                                        </span>
                                    </label>
                                    <input
                                        type="text"
                                        placeholder="SSP/UF"
                                        className="input input-bordered w-full"
                                        value={
                                            partnerFormData.rgIssuer
                                        }
                                        onChange={e =>
                                            setPartnerFormData({
                                                ...partnerFormData,
                                                rgIssuer:
                                                    e.target
                                                        .value,
                                            })
                                        }
                                    />
                                </div>
                            </div>
                        )}

                        {/* Endereço */}
                        <div className="divider">
                            <MdLocationOn className="text-lg text-base-content/50" />
                            <span className="text-sm text-base-content/70">
                                Endereço
                            </span>
                        </div>

                        <div className="form-control w-full sm:w-1/3">
                            <label className="label">
                                <span className="label-text">
                                    CEP
                                </span>
                            </label>
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="00000-000"
                                    className="input input-bordered w-full"
                                    value={formatCep(
                                        partnerFormData.zipCode
                                    )}
                                    onChange={e => {
                                        const raw =
                                            e.target.value
                                                .replace(
                                                    /\D/g,
                                                    ''
                                                )
                                                .slice(0, 8)
                                        setPartnerFormData({
                                            ...partnerFormData,
                                            zipCode: raw,
                                        })
                                        if (raw.length === 8)
                                            handlePartnerCepChange(
                                                raw
                                            )
                                    }}
                                />
                                {loadingPartnerCep && (
                                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 loading loading-spinner loading-sm"></span>
                                )}
                            </div>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="form-control w-full sm:flex-1">
                                <label className="label">
                                    <span className="label-text">
                                        Rua
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Rua, Avenida..."
                                    className="input input-bordered w-full"
                                    value={
                                        partnerFormData.street
                                    }
                                    onChange={e =>
                                        setPartnerFormData({
                                            ...partnerFormData,
                                            street: e.target
                                                .value,
                                        })
                                    }
                                />
                            </div>
                            <div className="form-control w-full sm:w-32">
                                <label className="label">
                                    <span className="label-text">
                                        Número
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Nº"
                                    className="input input-bordered w-full"
                                    value={
                                        partnerFormData.number
                                    }
                                    onChange={e =>
                                        setPartnerFormData({
                                            ...partnerFormData,
                                            number: e.target
                                                .value,
                                        })
                                    }
                                />
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    Complemento
                                </span>
                            </label>
                            <input
                                type="text"
                                placeholder="Apto, Sala, Bloco..."
                                className="input input-bordered w-full"
                                value={
                                    partnerFormData.complement
                                }
                                onChange={e =>
                                    setPartnerFormData({
                                        ...partnerFormData,
                                        complement:
                                            e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    Bairro
                                </span>
                            </label>
                            <input
                                type="text"
                                placeholder="Bairro"
                                className="input input-bordered w-full"
                                value={
                                    partnerFormData.neighborhood
                                }
                                onChange={e =>
                                    setPartnerFormData({
                                        ...partnerFormData,
                                        neighborhood:
                                            e.target.value,
                                    })
                                }
                            />
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="form-control w-full sm:flex-1">
                                <label className="label">
                                    <span className="label-text">
                                        Cidade
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Cidade"
                                    className="input input-bordered w-full"
                                    value={partnerFormData.city}
                                    onChange={e =>
                                        setPartnerFormData({
                                            ...partnerFormData,
                                            city: e.target
                                                .value,
                                        })
                                    }
                                />
                            </div>
                            <div className="form-control w-full sm:w-32">
                                <label className="label">
                                    <span className="label-text">
                                        UF
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="UF"
                                    className="input input-bordered w-full"
                                    maxLength={2}
                                    value={
                                        partnerFormData.state
                                    }
                                    onChange={e =>
                                        setPartnerFormData({
                                            ...partnerFormData,
                                            state: e.target.value.toUpperCase(),
                                        })
                                    }
                                />
                            </div>
                        </div>

                        {/* Dados Bancários  
                    <div className="divider">
                        <MdAccountBalance className="text-lg text-base-content/50" />
                        <span className="text-sm text-base-content/70">Dados Bancários</span>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="form-control w-full sm:w-32">
                            <label className="label">
                                <span className="label-text">Cód. Banco</span>
                            </label>
                            <input
                                type="text"
                                placeholder="000"
                                className="input input-bordered w-full"
                                value={partnerFormData.bankCode}
                                onChange={e => setPartnerFormData({ ...partnerFormData, bankCode: e.target.value })}
                            />
                        </div>
                        <div className="form-control w-full sm:flex-1">
                            <label className="label">
                                <span className="label-text">Nome do Banco</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Nome do Banco"
                                className="input input-bordered w-full"
                                value={partnerFormData.bankName}
                                onChange={e => setPartnerFormData({ ...partnerFormData, bankName: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="form-control w-full sm:flex-1">
                            <label className="label">
                                <span className="label-text">Agência</span>
                            </label>
                            <input
                                type="text"
                                placeholder="0000"
                                className="input input-bordered w-full"
                                value={partnerFormData.bankAgency}
                                onChange={e => setPartnerFormData({ ...partnerFormData, bankAgency: e.target.value })}
                            />
                        </div>
                        <div className="form-control w-full sm:flex-1">
                            <label className="label">
                                <span className="label-text">Conta</span>
                            </label>
                            <input
                                type="text"
                                placeholder="00000-0"
                                className="input input-bordered w-full"
                                value={partnerFormData.bankAccount}
                                onChange={e => setPartnerFormData({ ...partnerFormData, bankAccount: e.target.value })}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="form-control w-full sm:w-48">
                            <label className="label">
                                <span className="label-text">Tipo da Chave PIX</span>
                            </label>
                            <select
                                className="select select-bordered w-full"
                                value={partnerFormData.pixKeyType}
                                onChange={e => setPartnerFormData({ ...partnerFormData, pixKeyType: e.target.value })}
                            >
                                <option value="">Selecione</option>
                                <option value="CPF">CPF</option>
                                <option value="CNPJ">CNPJ</option>
                                <option value="EMAIL">Email</option>
                                <option value="PHONE">Telefone</option>
                                <option value="RANDOM">Chave Aleatória</option>
                            </select>
                        </div>
                        <div className="form-control w-full sm:flex-1">
                            <label className="label">
                                <span className="label-text">Chave PIX</span>
                            </label>
                            <input
                                type="text"
                                placeholder="Chave PIX"
                                className="input input-bordered w-full"
                                value={partnerFormData.pixKey}
                                onChange={e => setPartnerFormData({ ...partnerFormData, pixKey: e.target.value })}
                            />
                        </div>
                    </div> */}

                        {/* Contato Adicional */}
                        <div className="divider">
                            <MdSupportAgent className="text-lg text-base-content/50" />
                            <span className="text-sm text-base-content/70">
                                Contato Adicional
                            </span>
                        </div>

                        <div className="flex flex-col sm:flex-row gap-4">
                            <div className="form-control w-full sm:flex-1">
                                <label className="label">
                                    <span className="label-text">
                                        Nome do Contato
                                    </span>
                                </label>
                                <input
                                    type="text"
                                    placeholder="Nome"
                                    className="input input-bordered w-full"
                                    value={
                                        partnerFormData.contactName
                                    }
                                    onChange={e =>
                                        setPartnerFormData({
                                            ...partnerFormData,
                                            contactName:
                                                e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div className="form-control w-full sm:flex-1">
                                <label className="label">
                                    <span className="label-text">
                                        Telefone do Contato
                                    </span>
                                </label>
                                <input
                                    type="tel"
                                    placeholder="(00) 00000-0000"
                                    className="input input-bordered w-full"
                                    value={formatPhone(
                                        partnerFormData.contactPhone
                                    )}
                                    onChange={e => {
                                        const raw =
                                            e.target.value
                                                .replace(
                                                    /\D/g,
                                                    ''
                                                )
                                                .slice(0, 11)
                                        setPartnerFormData({
                                            ...partnerFormData,
                                            contactPhone: raw,
                                        })
                                    }}
                                />
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    Email do Contato
                                </span>
                            </label>
                            <input
                                type="email"
                                placeholder="contato@empresa.com"
                                className="input input-bordered w-full"
                                value={
                                    partnerFormData.contactEmail
                                }
                                onChange={e =>
                                    setPartnerFormData({
                                        ...partnerFormData,
                                        contactEmail:
                                            e.target.value,
                                    })
                                }
                            />
                        </div>

                        {/* Website e Observações */}
                        <div className="divider">
                            <span className="text-sm text-base-content/70">
                                Outros
                            </span>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    Website
                                </span>
                            </label>
                            <div className="relative">
                                <MdLanguage className="absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50 text-xl z-10 pointer-events-none" />
                                <input
                                    type="url"
                                    placeholder="https://www.exemplo.com"
                                    className="input input-bordered w-full pl-10"
                                    value={
                                        partnerFormData.website
                                    }
                                    onChange={e =>
                                        setPartnerFormData({
                                            ...partnerFormData,
                                            website:
                                                e.target.value,
                                        })
                                    }
                                />
                            </div>
                        </div>

                        <div className="form-control w-full">
                            <label className="label">
                                <span className="label-text">
                                    Observações
                                </span>
                            </label>
                            <textarea
                                placeholder="Observações gerais..."
                                className="textarea textarea-bordered w-full h-24"
                                value={partnerFormData.notes}
                                onChange={e =>
                                    setPartnerFormData({
                                        ...partnerFormData,
                                        notes: e.target.value,
                                    })
                                }
                            />
                        </div>

                        <button
                            className="btn btn-secondary w-full"
                            onClick={handleUpdatePartner}
                            disabled={savingPartner}
                        >
                            {savingPartner ? (
                                <span className="loading loading-spinner"></span>
                            ) : (
                                'Salvar Alterações'
                            )}
                        </button>
                    </>
                )}
            </div>
    )
}
